import { api, publicApi } from './client'
import type { AuthUser } from '../auth/store'

export interface Branding {
  name: string
  primaryColor: string | null
  logoUrl?: string
  /** 'EMAIL' | 'EMPLOYEE_ID' — 로그인 폼이 이메일을 받을지 사번을 받을지 결정 */
  loginType?: string
  plan?: string
  features?: {
    nfc: boolean
    leave: boolean
    stats: boolean
    drive: boolean
    ai: boolean
    /** 전자근로계약서 — 실제 차단은 plan(PRO 이상). 이건 조직의 opt-out 스위치 */
    contract: boolean
    /** 급여명세서 전달 — 위와 같다 */
    payslip: boolean
    /** 교육 증빙 — 위와 같다 */
    training: boolean
  }
  workStartTime?: string
  workEndTime?: string
  /** 조직별 화면 설정 (§4 B) — 값만 나른다 */
  uiConfig?: OrgUiConfig
  /** 'STANDARD' | 'FACTUAL'. 판정 분기는 AttendanceTime 한 곳에서만 한다 */
  judgmentMode?: 'STANDARD' | 'FACTUAL'
  /** 비교 기준 — 없으면 화면이 유예 0 으로 계산해 설정이 무효가 된다 */
  lateGraceMinutes?: number
  earlyLeaveMinutes?: number
}

/** 조직 설정 중 화면 취향에 해당하는 것들. 계약·분쟁과 얽히는 값은 여기 넣지 않는다 */
export interface OrgUiConfig {
  /** 근무시간·정시 기준을 화면에 보여줄지 (기본 ON) */
  showWorkHours?: boolean
  /** 홈의 '이번 달 현황' (기본 ON) */
  showMonthlySummary?: boolean
  /** 안내 배너 */
  noticeBanner?: { text: string; enabled: boolean }
  /** 직원이 고를 수 있는 부재 사유. 비우면 전체 */
  absenceReasons?: string[]
  /*
   * permissionPreset 은 두지 않는다 (2026-08-06 결정).
   *   "관리자도 기록 수정 가능" 은 부서장이 태깅 없이 부서원의 시각을 타이핑할 수
   *   있다는 뜻이고, 그건 대리출석의 가장 강한 형태다. 1기기 1사용자·임시비번
   *   ADMIN 전용·자가승인 차단·해시 체인이 전부 막아온 경로를 스위치 하나로 여는
   *   셈이라 설정 항목으로 두지 않는다. 필요한 고객이 나오면 계약 때 논의할 일이다.
   */
}

/** 서브도메인 기준 조직 브랜딩 (인증 불필요) */
export function getBranding() {
  return publicApi<Branding>('/api/organization/branding')
}

/** 회사 코드 기준 브랜딩 — 서브도메인이 없는 환경(앱·IP 접속)에서 사용 */
export function getBrandingByCode(code: string) {
  return publicApi<Branding & { code: string; subdomain: string }>(
    `/api/organization/branding?code=${encodeURIComponent(code)}`,
  )
}

interface LoginResult {
  accessToken: string
  refreshToken: string
  user: AuthUser & { id?: string }
}

export function login(identifier: string, password: string, loginType = 'EMAIL') {
  const body =
    loginType === 'EMPLOYEE_ID'
      ? { employeeId: identifier, password, loginType }
      : { email: identifier, password, loginType }

  return publicApi<LoginResult>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function logout() {
  return api<{ message: string }>('/api/auth/logout', { method: 'POST' })
}

// ⚠️ 서버에 /api/auth/me 는 없다. user 는 login·refresh 응답으로만 들어온다.
//    (apps/server/src/routes/auth.routes.ts 확인 — 2026-08-04)
//    역할·isActive 를 즉시 반영해야 하면 refresh 응답에 싣는 쪽이 맞다.
